import { makeAutoObservable, runInAction } from 'mobx';

import { FALLBACK_OTHER, FALLBACK_SPORTS, sportsService } from '@/services/sports';
import { usersService } from '@/services/users';
import { authStore } from '@/stores/auth.store';
import type { Sport } from '@/types/user';

class OnboardingStore {
  firstName = '';
  lastName = '';
  email = '';
  selectedSchool: string | null = null;
  // Sport input is hidden in the May-5 onboarding rebuild but the column
  // remains on the User model. Keep the state observable so the field
  // can be reactivated by uncommenting the input section without store
  // changes.
  sport: Sport | null = null;

  // Sports list (server-driven; bundled fallback used until first fetch lands)
  sports: readonly string[] = FALLBACK_SPORTS;
  otherSport: string = FALLBACK_OTHER;
  sportsLoaded = false;
  private sportsPromise: Promise<void> | null = null;

  submitting = false;
  submitError: string | null = null;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  /**
   * Fetch the canonical sports list. Safe to call from multiple mounts and
   * from React Strict Mode's dev double-invoke — concurrent calls share the
   * same in-flight promise instead of dispatching duplicate fetches.
   */
  async loadSports(): Promise<void> {
    if (this.sportsLoaded) return;
    if (this.sportsPromise) return this.sportsPromise;

    this.sportsPromise = (async () => {
      try {
        const { sports, other } = await sportsService.fetch();
        runInAction(() => {
          this.sports = sports;
          this.otherSport = other;
          this.sportsLoaded = true;
        });
      } catch {
        // Bundled fallback already populated; quietly stick with it.
      } finally {
        runInAction(() => {
          this.sportsPromise = null;
        });
      }
    })();
    return this.sportsPromise;
  }

  setName(first: string, last: string): void {
    this.firstName = first;
    this.lastName = last;
  }

  setEmail(email: string): void {
    this.email = email;
  }

  selectSchool(school: string | null): void {
    this.selectedSchool = school;
  }

  setSport(sport: Sport | null): void {
    this.sport = sport;
  }

  /** POST profile updates and refresh the auth user. Idempotent. */
  async submit(): Promise<void> {
    if (this.submitting) return;
    const trimmedEmail = this.email.trim();
    // Email is the May-5 onboarding gate (replaces school). The form's
    // submit button is gated on `isValidEmail`, but a direct retry of
    // `submit()` (e.g. after a transient network failure) bypasses the
    // form gate — fail loudly here instead of shipping a payload that
    // leaves the user trapped in `needs_onboarding`.
    if (trimmedEmail.length === 0) {
      throw new Error('Email is required to complete onboarding.');
    }
    this.submitting = true;
    this.submitError = null;
    try {
      await usersService.updateProfile({
        first_name: this.firstName.trim(),
        last_name: this.lastName.trim(),
        email: trimmedEmail,
        // School is now optional. Submit only when the user picked one.
        ...(this.selectedSchool ? { school: this.selectedSchool.trim() } : {}),
        // Sport input commented out — submit nothing rather than null.
        // ...(this.sport ? { sport: this.sport } : {}),
      });
      await authStore.refreshMe();
    } catch (err) {
      runInAction(() => {
        this.submitError =
          err instanceof Error ? err.message : 'Could not save your profile.';
      });
      throw err;
    } finally {
      runInAction(() => {
        this.submitting = false;
      });
    }
  }

  reset(): void {
    this.firstName = '';
    this.lastName = '';
    this.email = '';
    this.selectedSchool = null;
    this.sport = null;
    this.submitError = null;
  }
}

export const onboardingStore = new OnboardingStore();
